import { useEffect, useState } from "react";
import { Col, Nav, Row, Spinner } from "react-bootstrap"; // Removed raw Button import
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import { AlertType } from "../../../hooks/useAlert";
import { ImprovementPlanFormDto, ImprovementPlanFormSelectorsDto } from "../types";
import { createImprovementPlan, getImprovementPlanById, getSelectorsData, updateImprovementPlan } from "../api/improvementPlanAPI";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { getImprovementPlanFormSchema } from "../schema/improvementPlanSchema";
import ImprovementPlanDetailsTab from "../components/tabs/ImprovementPlanDetailsTab";
import ImprovementPlanActionsTab from "../components/tabs/ImprovementPlanActionsTab";
import ImprovementPlanDemandsTab from "../components/tabs/ImprovementPlanDemandsTab";
import ImprovementPlanDocumentsTab from "../components/tabs/ImprovementPlanDocumentsTab";
import ImprovementPlanRecommendationsTab from "../components/tabs/ImprovementPlanRecommendationsTab";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

import {
    clearErrors,
    resetForm,
    setError,
    setErrors,
    setField,
    setForm
} from "../../../redux/improvementPlanSlice";
import { GenericSaveButton } from "../../../components/common/GenericSaveButton";
import { useQueryClient } from "@tanstack/react-query";
import ImprovementPlanProposeActions from "../components/ImprovementPlanProposeActions";
import { convertUtc } from "../../../utils/dateUtils";

const emptySelectors: ImprovementPlanFormSelectorsDto = {
    responsibleOptions: [],
    originOptions: [],
    typeOptions: [],
    functionalAreaOptions: [],
    serviceOptions: [],
};

export default function ImprovementPlanForm() {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const navigate = useNavigate();
    const { improvementPlanId } = useParams<{ improvementPlanId: string }>();
    const isEditMode = Boolean(improvementPlanId);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const [selectors, setSelectors] = useState<ImprovementPlanFormSelectorsDto>(emptySelectors);
    const improvementPlanForm = useSelector((state: RootState) => state.improvementPlanForm.data);
    const proposedActionsPage = useSelector((state: RootState) => state.improvementPlanForm.proposedActionsPage);
    
    const [loadingSelectors, setLoadingSelectors] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");   
    const [alertType, setAlertType] = useState<AlertType>("success");
    const [tab, setTab] = useState("details");

    useEffect(() => {
        dispatch(resetForm());
        try {
            setLoadingSelectors(true);
            getSelectorsData()
                .then(selectorData => {
                    setSelectors(selectorData)
                })
                .catch((err: ApiErrorResponse) => {
                    setAlertMessage(err.message || t("common:error"));
                })
                .finally(() => setLoadingSelectors(false))
                
            if (improvementPlanId) {
                setLoadingDetails(true);
                getImprovementPlanById(Number(improvementPlanId))
                    .then(data => dispatch(setForm(data)))
                    .catch((err: ApiErrorResponse) => {
                        setAlertMessage(err.message || t("common:error"));
                    })
                    .finally(() => setLoadingDetails(false))
            }
        } catch (err: any) {
            setAlertMessage(err.message || t("common:error"));
            setLoadingSelectors(false);
            setLoadingDetails(false);
        }
    }, [improvementPlanId]);

    const updateField = <FormField extends keyof ImprovementPlanFormDto>(
        field: FormField,
        value: ImprovementPlanFormDto[FormField]
    ) => {
        if (field === "startDate" || field === "endDate" || field === "creationDate" || field === "expectedEndDate") {
            value = convertUtc(value);
        }
        dispatch(setField({ field, value }));
        dispatch(setError({ field, message: undefined }));
    };

    const parseNumberOrUndefined = (value: string) => (value === "" ? undefined : Number(value));

    const onSubmit = async () => {
        const schema = getImprovementPlanFormSchema(t);
        dispatch(clearErrors());

        try {
            await schema.validate(improvementPlanForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        validationErrors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                validationErrors[err.path] = err.message;
            }
            dispatch(setErrors(validationErrors));
            return;
        }
        
        setLoadingDetails(true);
        try {
            if (isEditMode && improvementPlanId) {
                await updateImprovementPlan(Number(improvementPlanId), improvementPlanForm);
            } else {
                await createImprovementPlan(improvementPlanForm);
            }

            queryClient.invalidateQueries({
                queryKey: ['getImprovementPlanIndicators'],
                exact: false,
                refetchType: 'all',
            });

            queryClient.invalidateQueries({
                queryKey: ['improvementPlanPieChart'],
                exact: false,
                refetchType: 'all',
            });

            queryClient.invalidateQueries({
                queryKey: ['getImprovementPlanStatusAndTypeBarChart'],
                exact: false,
                refetchType: 'all',
            });

            navigate("/improvement-plans/historic", {
                state: {
                    message: t("improvementPlan:form.savedSuccessfully"),
                    shouldRefresh: true,
                },
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message || t("common:error");
            setAlertType("danger");
            setAlertMessage(errorMessage);
            setShowAlert(true);
        } finally {
            setLoadingDetails(false);
        }
    };
    
    const onCancel = () => {
        dispatch(resetForm());
        navigate("/improvement-plans/historic");
    };

    return (
        <div>
            {(loadingSelectors || loadingDetails) && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row className="mb-2">
                <Col xs={8} md={8}>
                    <h2>
                        {isEditMode
                            ? `${t("improvementPlan:form.editTitle")} #${improvementPlanId}`
                            : t("improvementPlan:form.newTitle")}
                    </h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton 
                        onClick={onSubmit}
                        onCancelClick={onCancel}
                        cancelLabel={isEditMode ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                        saveLabel={t("common:save")}
                        disabled={loadingSelectors || loadingDetails}
                        showSaveButton={!proposedActionsPage}
                        showCancelButton={!proposedActionsPage}                        
                    />
                </Col>
            </Row>

            {showAlert && (
                <AlertMessage
                    message={alertMessage}
                    type={alertType}
                    onClose={() => setShowAlert(false)}
                />
            )}

            <Nav variant="tabs" activeKey={tab}>
                <Nav.Item>
                    <Nav.Link
                        eventKey="details"
                        className={`survey-tab-link ${tab === "details" ? "active-tab" : ""}`}
                        onClick={() => setTab("details")}
                        disabled={proposedActionsPage}
                    >
                        {t("improvementPlan:tabs.details")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="actions"
                        className={`survey-tab-link ${tab === "actions" ? "active-tab" : ""}`}
                        onClick={() => setTab("actions")}
                        disabled={proposedActionsPage}
                    >
                        {t("improvementPlan:tabs.actions")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="demands"
                        className={`survey-tab-link ${tab === "demands" ? "active-tab" : ""}`}
                        onClick={() => setTab("demands")}
                        disabled={proposedActionsPage}
                    >
                        {t("improvementPlan:tabs.demands")}
                    </Nav.Link>
                </Nav.Item>
                {/* TODO: Descomentar este tab cuando se tenga que crear la sección. Actualmente permace oculta */}
                {/* <Nav.Item>
                    <Nav.Link
                        eventKey="recommendations"
                        className={`survey-tab-link ${tab === "recommendations" ? "active-tab" : ""}`}
                        onClick={() => setTab("recommendations")}
                    >
                        {t("improvementPlan:tabs.recommendations")}
                    </Nav.Link>
                </Nav.Item> */}
                <Nav.Item>
                    <Nav.Link
                        eventKey="documents"
                        className={`survey-tab-link ${tab === "documents" ? "active-tab" : ""}`}
                        onClick={() => setTab("documents")}
                        disabled={proposedActionsPage}
                    >
                        {t("improvementPlan:tabs.documents")}
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            <div className="mt-3">
                {tab === "details" && (
                    <ImprovementPlanDetailsTab
                        selectors={selectors}
                        updateField={updateField}
                        parseNumberOrUndefined={parseNumberOrUndefined}
                    />
                )}
                {tab === "actions" && (
                    proposedActionsPage ?
                        <ImprovementPlanProposeActions /> :
                        <ImprovementPlanActionsTab responsibleOptions={selectors.responsibleOptions} />
                )}
                {tab === "demands" && <ImprovementPlanDemandsTab assignedToOptions={selectors.responsibleOptions} />}
                {tab === "recommendations" && <ImprovementPlanRecommendationsTab />}
                {tab === "documents" && <ImprovementPlanDocumentsTab />}
            </div>
        </div>
    );
}